
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Users, Plus, Edit, Trash2, Loader2, Share2, Copy, Building2, Target, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminPaginationControls from '@/components/AdminPaginationControls';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';
import { useCompany } from '@/contexts/CompanyContext';
import { useToast } from '@/hooks/use-toast';

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  created_at: string;
  updated_at: string;
}

interface AdminProfilesTabProps {
  idealProfiles: IdealProfile[];
  onDeleteProfile: (profile: IdealProfile) => void;
  deletingProfileIds: Set<string>;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    resetToFirstPage: () => void;
  };
  totalCount: number;
}

// Helper function to format trait ranges
const formatTraitRange = (min: number, max: number): string => {
  if (min === max) {
    return `${min}%`;
  }
  return `${min}%-${max}%`;
};

const AdminProfilesTab: React.FC<AdminProfilesTabProps> = ({
  idealProfiles,
  onDeleteProfile,
  deletingProfileIds,
  pagination,
  totalCount
}) => {
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const { toast } = useToast();
  const companySlug = currentCompany?.company_slug || null;

  const handleCopyLink = async (linkType: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    
    let link = '';
    let linkName = '';
    
    switch (linkType) {
      case 'registration':
        link = generateRegistrationLink(companySlug);
        linkName = 'Registration';
        break;
      case 'workBehavior':
        link = generateWorkBehaviorLink(companySlug);
        linkName = 'Work Behavior Assessment';
        break;
      case 'cognitive':
        link = generateCognitiveAssessmentLink(companySlug);
        linkName = 'Cognitive Assessment';
        break;
    }
    
    const success = await copyToClipboard(link);
    if (success) {
      toast({
        title: "Link Copied!",
        description: `${linkName} link copied to clipboard`
      });
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleEditProfile = (profileId: string) => {
    navigate(`/ideal/${profileId}`);
  };

  return (
    <Card className="bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-card-foreground">
            <Users className="h-5 w-5" />
            Ideal Job Profiles
          </CardTitle>
          <Button onClick={() => navigate('/ideal/new')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Profile
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {idealProfiles.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Profile Name</TableHead>
                    <TableHead>Dom</TableHead>
                    <TableHead>Ext</TableHead>
                    <TableHead>Pat</TableHead>
                    <TableHead>Form</TableHead>
                    <TableHead>Obj</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {idealProfiles.map((profile) => {
                    const isDeleting = deletingProfileIds.has(profile.id);
                    return (
                      <TableRow 
                        key={profile.id} 
                        className={`hover:bg-muted/50 ${isDeleting ? 'opacity-50' : ''}`}
                      >
                        <TableCell className="font-medium">{profile.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                            {formatTraitRange(profile.dominance_min, profile.dominance_max)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                            {formatTraitRange(profile.extraversion_min, profile.extraversion_max)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                            {formatTraitRange(profile.patience_min, profile.patience_max)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                            {formatTraitRange(profile.formality_min, profile.formality_max)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/20">
                            {formatTraitRange(profile.objectivity_min, profile.objectivity_max)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(profile.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-primary hover:bg-primary/10"
                                  disabled={isDeleting || !companySlug}
                                >
                                  <Share2 className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-72 bg-background border shadow-lg z-50">
                                <DropdownMenuItem 
                                  onClick={() => handleCopyLink('registration')}
                                  className="flex items-center justify-between cursor-pointer bg-primary/5 border-l-2 border-primary"
                                >
                                  <div className="flex items-center gap-2 whitespace-nowrap">
                                    <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="font-medium">Full Assessment</span>
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0 flex-shrink-0">Recommended</Badge>
                                  </div>
                                  <Copy className="h-4 w-4 flex-shrink-0" />
                                </DropdownMenuItem>
                                
                                <DropdownMenuSeparator />
                                
                                <DropdownMenuItem 
                                  onClick={() => handleCopyLink('workBehavior')}
                                  className="flex items-center justify-between cursor-pointer"
                                >
                                  <div className="flex items-center gap-2 whitespace-nowrap">
                                    <Target className="h-4 w-4 flex-shrink-0" />
                                    <span>Work Behavior Only</span>
                                  </div>
                                  <Copy className="h-4 w-4 flex-shrink-0" />
                                </DropdownMenuItem>
                                
                                <DropdownMenuItem 
                                  onClick={() => handleCopyLink('cognitive')}
                                  className="flex items-center justify-between cursor-pointer"
                                >
                                  <div className="flex items-center gap-2 whitespace-nowrap">
                                    <Brain className="h-4 w-4 flex-shrink-0" />
                                    <span>Cognitive Only</span>
                                  </div>
                                  <Copy className="h-4 w-4 flex-shrink-0" />
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditProfile(profile.id)}
                              className="text-orange-500 hover:bg-orange-500/10"
                              disabled={isDeleting}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => onDeleteProfile(profile)}
                              className="text-red-500 hover:bg-red-500/10"
                              disabled={isDeleting}
                            >
                              {isDeleting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            {totalCount > 0 && (
              <AdminPaginationControls
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                pageSize={pagination.pageSize}
                totalItems={totalCount}
                startIndex={pagination.startIndex}
                endIndex={pagination.endIndex}
                hasNextPage={pagination.hasNextPage}
                hasPreviousPage={pagination.hasPreviousPage}
                onPageChange={pagination.setCurrentPage}
                onPageSizeChange={pagination.setPageSize}
                onNextPage={pagination.goToNextPage}
                onPreviousPage={pagination.goToPreviousPage}
              />
            )}
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p className="mb-4">No ideal profiles created yet.</p>
            <Button onClick={() => navigate('/ideal/new')}>
              Create Your First Profile
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminProfilesTab;
